import { promotionalOfferCrudConfig } from '@/config/objects/promotional-offers';
import { useUser } from '@clerk/clerk-react';
import {
  Button,
  CrudFormView,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormPageLayout,
  Textarea,
  useRequireOrganization,
  type CrudFormViewRef,
} from '@startupquickstart/frontend';
import { ArrowLeft, Copy, FileUp, Save } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const VALID_IMPORT_KEYS = [
  'name', 'title', 'description', 'status', 'isActive', 'redemptionType',
  'discountType', 'discountPercentage', 'maxDiscountAmount', 'discountValue',
  'validFrom', 'validUntil', 'maxUserRedemptions', 'maxRedemptions',
  'termsAndConditions', 'promotionalCode', 'minUsers', 'maxUsers',
  'usageLimit', 'hours',
];

/**
 * Promotional Offer create/edit form page using FormPageLayout
 */
export function PromotionalOfferFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isInternal } = useRequireOrganization();
  const { user } = useUser();
  const canImport = isInternal || user?.primaryEmailAddress?.emailAddress?.endsWith('@destinationtours.com');
  const formRef = useRef<CrudFormViewRef>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const handleSuccess = (promotionalOffer: { id: string }) => {
    if (isEdit) {
      navigate(-1);
    } else {
      navigate(`/promotional-offers/${promotionalOffer.id}`);
    }
  };

  const handleCancel = () => {
    navigate(isEdit ? `/promotional-offers/${itemId}` : '/promotional-offers');
  };

  const handleSave = () => {
    formRef.current?.submit();
  };

  const handleImportJson = () => {
    try {
      const parsed = JSON.parse(importJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        toast.error('JSON must be an object');
        return;
      }

      const filtered: Record<string, unknown> = {};
      for (const key of VALID_IMPORT_KEYS) {
        if (key in parsed) {
          filtered[key] = parsed[key];
        }
      }

      if (Object.keys(filtered).length === 0) {
        toast.error('No valid fields found in JSON');
        return;
      }

      formRef.current?.setFormData(filtered);
      setImportDialogOpen(false);
      setImportJson('');
      toast.success(`Imported ${Object.keys(filtered).length} field(s)`);
    } catch {
      toast.error('Invalid JSON format');
    }
  };

  const handleCopySchema = () => {
    const schema = {
      name: 'Summer Pool Party Special',
      title: '20% Off Pool Passes',
      description: '<p>Enjoy a refreshing discount on pool access this summer.</p>',
      status: 'published',
      isActive: true,
      redemptionType: 'barcode',
      discountType: 'percentage',
      discountPercentage: 20,
      maxDiscountAmount: 50,
      discountValue: 10,
      validFrom: '2025-06-01T00:00:00.000Z',
      validUntil: '2025-09-30T23:59:59.000Z',
      maxUserRedemptions: 3,
      maxRedemptions: 500,
      promotionalCode: 'SUMMER25',
      minUsers: 1,
      maxUsers: 10,
      usageLimit: 1000,
      termsAndConditions: '<p>Valid for hotel guests only. Cannot be combined with other offers.</p>',
      hours: {
        monday: { ranges: [{ open: '09:00', close: '17:00' }] },
        tuesday: { ranges: [{ open: '09:00', close: '17:00' }] },
        wednesday: { ranges: [{ open: '09:00', close: '17:00' }] },
        thursday: { ranges: [{ open: '09:00', close: '17:00' }] },
        friday: { ranges: [{ open: '09:00', close: '21:00' }] },
        saturday: { ranges: [{ open: '10:00', close: '21:00' }] },
        sunday: { isClosed: true },
      },
    };

    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    toast.success('Schema copied to clipboard');
  };

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Promotional Offer' : 'Create Promotional Offer'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canImport && !itemId && (
            <>
              <Dialog open={importDialogOpen} onOpenChange={(open) => {
                setImportDialogOpen(open);
                if (open) setImportJson('');
              }}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" icon={FileUp}>
                    Import JSON
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import JSON</DialogTitle>
                    <DialogDescription>
                      Paste promotional offer JSON data to pre-fill the form fields.
                    </DialogDescription>
                  </DialogHeader>
                  <Textarea
                    value={importJson}
                    onChange={e => setImportJson(e.target.value)}
                    placeholder={'{\n  "name": "Summer Special",\n  "title": "20% Off",\n  "discountType": "percentage",\n  "discountPercentage": 20\n}'}
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <DialogFooter>
                    <div className="flex sm:justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleImportJson}>
                        Import
                      </Button>
                    </div>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button type="button" variant="outline" icon={Copy} onClick={handleCopySchema}>
                Copy Schema
              </Button>
            </>
          )}
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={formRef.current?.isPending}
            icon={Save}
          >
            {isEdit ? 'Save Changes' : 'Create Offer'}
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content — CrudFormView renders card sections (or skeletons while loading) */}
      <FormPageLayout.Content>
        <CrudFormView
          ref={formRef}
          config={promotionalOfferCrudConfig}
          itemId={itemId}
          onSuccess={handleSuccess}
          renderHeader={false}
          renderFooter={false}
        />
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={formRef.current?.lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={formRef.current?.isPending}
            icon={Save}
          >
            {isEdit ? 'Save Changes' : 'Create Offer'}
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
